import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle, Loader2, Package, FileText } from 'lucide-react';
import { fetchOrderById } from '../services/orderService';
import { getInvoiceDownloadUrl } from '../services/invoiceService';

const STATUS_LABELS = {
  pending: 'Pending',
  paid: 'Confirmed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  shipped: 'Shipped',
  delivered: 'Delivered',
};

export default function OrderSuccess() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchOrderById(orderId)
      .then(setOrder)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [orderId]);

  const handleDownloadInvoice = async () => {
    if (!order) return;
    setInvoiceError('');
    setInvoiceLoading(true);
    try {
      const { signedUrl } = await getInvoiceDownloadUrl({ orderId: order.id });
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      setInvoiceError(err.message || 'Invoice will be available shortly. Try again in a moment.');
    } finally {
      setInvoiceLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </main>
    );
  }

  if (!order) {
    return (
      <main className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <p className="text-gray-500 mb-6">Order not found.</p>
        <Link to="/shop" className="text-xs tracking-widest uppercase border-b border-black pb-0.5">
          Continue Shopping
        </Link>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      <section className="bg-[#fdf8f6] py-16 sm:py-20 text-center px-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white border border-green-100 mb-6">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Thank You</p>
        <h1 className="font-display text-4xl sm:text-5xl font-light mb-3">Order Placed</h1>
        <p className="text-sm text-gray-500 font-light max-w-md mx-auto">
          Your payment was successful. We&apos;ll send you updates on your order status.
        </p>
      </section>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="border border-gray-100 p-6 sm:p-8 space-y-6">
          <div className="flex items-start gap-4">
            <Package size={20} className="text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-1">Order ID</p>
              <p className="font-medium text-lg tracking-wide">{order.order_number}</p>
            </div>
            <span className="text-xs tracking-widest uppercase px-3 py-1 bg-green-50 text-green-700 border border-green-100">
              {STATUS_LABELS[order.status] || order.status}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-6 text-sm">
            <div>
              <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-1">Total Paid</p>
              <p className="font-medium">₹{order.total_amount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-1">Payment ID</p>
              <p className="font-light text-gray-600 text-xs break-all">
                {order.razorpay_payment_id || '—'}
              </p>
            </div>
          </div>

          {order.shipping_address && (
            <div className="text-sm border-t border-gray-100 pt-6">
              <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-2">Delivering to</p>
              <p className="font-medium">{order.shipping_name}</p>
              <p className="text-gray-500 font-light mt-1 leading-relaxed">
                {order.shipping_address}, {order.shipping_city} — {order.shipping_pincode}
              </p>
              <p className="text-gray-500 font-light mt-1">{order.shipping_phone}</p>
            </div>
          )}

          {order.items?.length > 0 && (
            <div className="border-t border-gray-100 pt-6">
              <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-4">Items</p>
              <div className="space-y-3">
                {order.items.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.product_name} × {item.quantity}
                    </span>
                    <span>₹{item.line_total.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {(order.status === 'paid' || order.invoice_url) && (
          <div className="mt-6 flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={handleDownloadInvoice}
              disabled={invoiceLoading}
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-medium border border-black px-6 py-3 hover:bg-black hover:text-white transition-colors disabled:opacity-50"
            >
              {invoiceLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <FileText size={14} />
              )}
              Download Invoice
            </button>
            {order.invoice_number && (
              <p className="text-xs text-gray-400">{order.invoice_number}</p>
            )}
            {invoiceError && <p className="text-xs text-red-500">{invoiceError}</p>}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
          <Link
            to="/track-order"
            className="text-center px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium border border-black hover:bg-black hover:text-white transition-colors"
          >
            Track Order
          </Link>
          <Link
            to="/shop"
            className="text-center px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium bg-black text-white hover:bg-gray-900 transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </main>
  );
}
